import React, { useCallback, useId } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, func, number, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import formatPrice from 'Utils/formatPrice'

const TRANSLATION_KEY_PREFIX = 'subscription_checkout.plan_selector'

function PlanSelector({ plans, selectedKey, onChange: handleChange, locale = 'en' }) {
  const groupName = useId()
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  const badgeClass = (badge) => classnames(
    'plan-selector__badge cgt-overline-sm',
    badge === 'save_60' && 'plan-selector__badge--savings',
    badge === 'cancel_anytime' && 'plan-selector__badge--anytime',
  )

  return (
    <div
      className="plan-selector"
      role="radiogroup"
      aria-label={translate(`${TRANSLATION_KEY_PREFIX}.section_title`)}
    >
      <div className="plan-selector__title cgt-overline-sm">
        {translate(`${TRANSLATION_KEY_PREFIX}.section_title`)}
      </div>

      <div className="plan-selector__row">
        {plans.map((plan) => {
          const isSelected = plan.key === selectedKey
          const planName = translate(`${TRANSLATION_KEY_PREFIX}.names.${plan.key}`)
          const subtitle = translate(`${TRANSLATION_KEY_PREFIX}.subtitles.${plan.subtitle}`)
          const badgeLabel = translate(`${TRANSLATION_KEY_PREFIX}.badges.${plan.badge}`)
          const cardClass = classnames(
            'plan-selector__option',
            isSelected && 'plan-selector__option--selected',
          )

          const inputId = `subscription-plan-${plan.key}`

          return (
            <label key={plan.key} htmlFor={inputId} className={cardClass}>
              <input
                id={inputId}
                type="radio"
                className="plan-selector__option-input"
                name={groupName}
                value={plan.key}
                checked={isSelected}
                onChange={() => handleChange(plan.key)}
                aria-label={translate(`${TRANSLATION_KEY_PREFIX}.select_aria`, { name: planName })}
              />

              <div className="plan-selector__option-header">
                <span className="plan-selector__option-name cgt-body-base-semibold">
                  {planName}
                </span>
                <span className={badgeClass(plan.badge)}>{badgeLabel}</span>
              </div>

              <span className="plan-selector__option-price-row">
                <span className="plan-selector__current-price cgt-heading-300">{formatPrice(plan.price)}</span>
                <span className="plan-selector__period cgt-body-base">
                  {translate('subscription_checkout.order_summary.price_unit')}
                </span>
              </span>

              <span className="plan-selector__option-subtitle cgt-caption">
                {subtitle}
              </span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

PlanSelector.propTypes = {
  plans: arrayOf(
    shape({
      key: string.isRequired,
      price: number.isRequired,
      badge: string,
      subtitle: string,
    }),
  ).isRequired,
  selectedKey: string.isRequired,
  onChange: func.isRequired,
  locale: string,
}

export default PlanSelector
